import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/auth";
import { supabase } from "@/integrations/supabase/client";
import PhoneVerificationModal from "@/components/profile/PhoneVerificationModal";

// Only show on these routes — nowhere else
const ALLOWED_ROUTES = ["/checkout"];

const PhonePromptManager = () => {
  const { user, isAdmin, isSuperAdmin, hasAnyRole } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const checkedUserRef = useRef<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    if (!user?.id) {
      checkedUserRef.current = null;
      return;
    }

    // Only show on checkout page
    if (!ALLOWED_ROUTES.some((route) => location.pathname.startsWith(route))) return;

    // Never show to admin/staff users
    if (isAdmin || isSuperAdmin) return;
    if (
      hasAnyRole([
        "admin",
        "super_admin",
        "order_processor",
        "inventory_manager",
        "customer_support",
        "content_manager",
        "financial_officer",
      ])
    )
      return;

    // Only check once per user session
    if (checkedUserRef.current === user.id) return;
    checkedUserRef.current = user.id;

    const checkPhone = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("phone, phone_verified")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!data?.phone) {
        // Delay slightly — give the page time to settle first
        setTimeout(() => setShowModal(true), 3000);
      }
    };

    checkPhone();
  }, [user?.id, isAdmin, isSuperAdmin, location.pathname]);

  return (
    <PhoneVerificationModal
      isOpen={showModal}
      onClose={() => setShowModal(false)}
      onVerified={() => setShowModal(false)}
    />
  );
};

export default PhonePromptManager;
